import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useList, useCustom, useCustomMutation } from '@refinedev/core';
import type {
  Broadcast,
  BroadcastCreatePayload,
  BroadcastFilters,
  BroadcastMedia,
  BroadcastButton,
  BroadcastTranslation,
  BroadcastPreviewResult,
  BroadcastPayload,
  DeliveryBreakdown,
} from '../types';
import type { NotificationChannel } from '@masters-club/types';
import type { SegmentDefinition } from '@masters-club/validation';
import type { EmailDoc } from '@/components/email-editor';
import { isSegmentDefinition, isBroadcastFilters } from '../utils/segment-utils';
import type { SubscriptionPlan } from '../../subscription-plans/types';
import type { Course } from '../../courses/types';
import type { TelegramChannel } from '../../telegram-channels/types';

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
};

interface UseBroadcastFormOptions {
  initialData?: Broadcast | null;
}

export function normalizeFilters(filters: BroadcastFilters): BroadcastFilters | null {
  const normalized: BroadcastFilters = {
    isActive: filters.isActive,
    hasEmail: filters.hasEmail,
    hasTelegram: filters.hasTelegram,
    hasActiveSubscription: filters.hasActiveSubscription,
    registeredAfter: filters.registeredAfter,
    registeredBefore: filters.registeredBefore,
    languages: filters.languages?.filter(Boolean),
    productIds: filters.productIds?.filter(Boolean),
    courseIds: filters.courseIds?.filter(Boolean),
    channelIds: filters.channelIds?.filter(Boolean),
    userIds: filters.userIds && filters.userIds.length > 0 ? filters.userIds : undefined,
  };

  const hasValues = Object.values(normalized).some((value) => {
    if (Array.isArray(value)) { return value.length > 0; }
    return value !== undefined && value !== null && value !== '';
  });

  return hasValues ? normalized : null;
}

export function useBroadcastForm(options: UseBroadcastFormOptions = {}) {
  const { initialData } = options;

  // ─── Form state ──────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [parseMode, setParseMode] = useState<'Markdown' | 'HTML' | 'none'>('Markdown');
  const [channels, setChannels] = useState<NotificationChannel[]>(['TELEGRAM']);
  // Бот, через которого уйдёт TELEGRAM-рассылка. null = первый активный (раньше — isPrimary).
  const [botId, setBotId] = useState<number | null>(null);
  // definition и selectedUserIds живут внутри TabRecipients (через ref), чтобы не вызывать ререндер parent
  const recipientDataRef = useRef<{ definition: SegmentDefinition; selectedUserIds: number[] }>({
    definition: { groupsOp: 'AND', groups: [] },
    selectedUserIds: [],
  });
  // Число получателей считается в TabRecipients (/count-recipients) и
  // пробрасывается сюда для отображения в сводке (BroadcastSummary).
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const [mediaEnabled, setMediaEnabled] = useState(false);
  const [mediaType, setMediaType] = useState<BroadcastMedia['type']>('photo');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaCaption, setMediaCaption] = useState('');
  const [mediaParseMode, setMediaParseMode] = useState<'Markdown' | 'HTML' | 'none'>('none');

  // Telegram buttons
  const [buttons, setButtons] = useState<BroadcastButton[]>([]);
  // Email buttons
  const [emailButtons, setEmailButtons] = useState<BroadcastButton[]>([]);
  // App buttons
  const [appButtons, setAppButtons] = useState<BroadcastButton[]>([]);

  // Email visual editor (опционально для канала EMAIL)
  const [emailVisualEnabled, setEmailVisualEnabled] = useState(false);
  const [emailHtml, setEmailHtml] = useState('');
  const [emailDoc, setEmailDoc] = useState<EmailDoc | undefined>();

  // Translations state
  const [translations, setTranslations] = useState<Map<string, string>>(new Map());
  const [activeTranslationTab, setActiveTranslationTab] = useState<string>('default');

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLanguage, setPreviewLanguage] = useState<string>('');
  const [previewResult, setPreviewResult] = useState<BroadcastPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Delivery breakdown state (вкладка «Доставка»: кто с какого бота / на email получит)
  const [breakdown, setBreakdown] = useState<DeliveryBreakdown | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);

  // Populate guard (edit mode)
  const [isPopulated, setIsPopulated] = useState(false);

  const { mutate: fetchPreview } = useCustomMutation<{ data: BroadcastPreviewResult }>();
  const { mutate: fetchBreakdown } = useCustomMutation<DeliveryBreakdown>();

  // ─── Data fetching ─────────────────────────────────────────────────────────

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
    pagination: { pageSize: 100 },
  });

  const { result: variablesResult } = useCustom<{ success: boolean; data: string[] }>({
    url: '/admin/broadcasts/variables',
    method: 'get',
  });

  const { result: plansResult } = useList<SubscriptionPlan>({
    resource: 'subscription-plans',
    pagination: { pageSize: 100 },
    sorters: [{ field: 'order', order: 'asc' }],
  });

  const { result: coursesResult } = useList<Course>({
    resource: 'courses',
    pagination: { pageSize: 100 },
    sorters: [{ field: 'createdAt', order: 'desc' }],
  });

  const { result: channelsResult } = useList<TelegramChannel>({
    resource: 'telegram-channels',
    pagination: { pageSize: 100 },
    sorters: [{ field: 'title', order: 'asc' }],
  });

  const languages = useMemo(() => languagesResult?.data ?? [], [languagesResult?.data]);

  const availableVariables = useMemo(() => {
    const responseData = variablesResult?.data?.data;
    if (Array.isArray(responseData)) {
      return responseData;
    }
    return [
      '{{user.firstName}}', '{{user.lastName}}', '{{user.username}}',
      '{{user.displayName}}', '{{user.email}}', '{{user.telegramId}}',
      '{{user.language}}', '{{now}}',
    ];
  }, [variablesResult]);

  const plans = useMemo(() => plansResult?.data ?? [], [plansResult?.data]);
  const courses = useMemo(() => coursesResult?.data ?? [], [coursesResult?.data]);
  const telegramChannels = useMemo(() => channelsResult?.data ?? [], [channelsResult?.data]);

  // ─── Populate from broadcast (edit mode) ─────────────────────────────────

  useEffect(() => {
    if (!initialData || isPopulated) { return; }

    setName(initialData.name);
    setMessageTemplate(initialData.messageTemplate);
    setParseMode(initialData.parseMode ?? 'none');
    setChannels(initialData.channels);
    setBotId(initialData.botId ?? null);

    // Загружаем filters из БД — может быть legacy BroadcastFilters или SegmentDefinition.
    // Если это SegmentDefinition — используем как есть.
    // Если legacy — используем пустую SegmentDefinition (legacy-рассылки управляются через backend).
    const loadedFilters = initialData.filters;
    let loadedDefinition: SegmentDefinition = { groupsOp: 'AND', groups: [] };
    let loadedUserIds: number[] = [];

    if (isSegmentDefinition(loadedFilters)) {
      loadedDefinition = loadedFilters;
    } else if (isBroadcastFilters(loadedFilters)) {
      // legacy BroadcastFilters — извлекаем userIds как selectedUserIds,
      // definition остаётся пустой (backend выполнит legacy путь через parseFilters)
      loadedUserIds = loadedFilters.userIds ?? [];
    }

    recipientDataRef.current = {
      definition: loadedDefinition,
      selectedUserIds: loadedUserIds,
    };
    setScheduledAt(initialData.scheduledAt ?? '');

    const media = initialData.payload?.telegram?.media;
    if (media) {
      setMediaEnabled(true);
      setMediaType(media.type);
      setMediaUrl(media.url);
      setMediaCaption(media.caption ?? '');
      setMediaParseMode(media.parseMode ?? 'none');
    }

    const loadedButtons = initialData.payload?.telegram?.buttons;
    if (loadedButtons && loadedButtons.length > 0) { setButtons(loadedButtons); }

    const loadedEmailButtons = initialData.payload?.email?.buttons;
    if (loadedEmailButtons && loadedEmailButtons.length > 0) { setEmailButtons(loadedEmailButtons); }

    const loadedEmailHtml = initialData.payload?.email?.html;
    const loadedEmailDoc = initialData.payload?.email?.docJson;
    if (loadedEmailHtml && loadedEmailHtml.trim().length > 0) {
      setEmailVisualEnabled(true);
      setEmailHtml(loadedEmailHtml);
      setEmailDoc(loadedEmailDoc);
    }

    const loadedAppButtons = initialData.payload?.app?.buttons;
    if (loadedAppButtons && loadedAppButtons.length > 0) { setAppButtons(loadedAppButtons); }

    if (initialData.translations) {
      const translationsMap = new Map<string, string>();
      for (const [langCode, translation] of Object.entries(initialData.translations)) {
        if (translation.messageTemplate.trim()) {
          translationsMap.set(langCode, translation.messageTemplate);
        }
      }
      setTranslations(translationsMap);
    }

    setIsPopulated(true);
  }, [initialData, isPopulated]);

  // Set default preview language from first available language
  useEffect(() => {
    if (languages.length > 0 && !previewLanguage) {
      setPreviewLanguage(languages[0].code);
    }
  }, [languages, previewLanguage]);

  // ─── Stable callbacks ──────────────────────────────────────────────────────

  const toggleChannel = useCallback((checked: boolean, channel: NotificationChannel) => {
    setChannels((prev) => {
      if (checked) {
        return prev.includes(channel) ? prev : [...prev, channel];
      }
      return prev.filter((item) => item !== channel);
    });
  }, []);

  // Callback для TabRecipients — синхронизирует ref без ререндера parent.
  // recipientCount пишется в state (реактивно), т.к. нужен в сводке.
  const handleRecipientDataChange = useCallback(
    (data: { definition: SegmentDefinition; selectedUserIds: number[]; recipientCount?: number | null }) => {
      recipientDataRef.current = { definition: data.definition, selectedUserIds: data.selectedUserIds };
      if (data.recipientCount !== undefined) {
        setRecipientCount(data.recipientCount);
      }
    },
    [],
  );

  // Telegram buttons
  const addButton = useCallback(() => {
    setButtons((prev) => [...prev, { text: '', url: '', callbackData: '' }]);
  }, []);
  const updateButton = useCallback((index: number, next: Partial<BroadcastButton>) => {
    setButtons((prev) =>
      prev.map((button, i) => (i === index ? { ...button, ...next } : button))
    );
  }, []);
  const removeButton = useCallback((index: number) => {
    setButtons((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Email buttons
  const addEmailButton = useCallback(() => {
    setEmailButtons((prev) => [...prev, { text: '', url: '' }]);
  }, []);
  const updateEmailButton = useCallback((index: number, next: Partial<BroadcastButton>) => {
    setEmailButtons((prev) =>
      prev.map((button, i) => (i === index ? { ...button, ...next } : button))
    );
  }, []);
  const removeEmailButton = useCallback((index: number) => {
    setEmailButtons((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // App buttons
  const addAppButton = useCallback(() => {
    setAppButtons((prev) => [...prev, { text: '', url: '' }]);
  }, []);
  const updateAppButton = useCallback((index: number, next: Partial<BroadcastButton>) => {
    setAppButtons((prev) =>
      prev.map((button, i) => (i === index ? { ...button, ...next } : button))
    );
  }, []);
  const removeAppButton = useCallback((index: number) => {
    setAppButtons((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleEmailEditorChange = useCallback(
    (next: { docJson: EmailDoc; htmlTemplate: string }) => {
      setEmailHtml((prev) => (prev === next.htmlTemplate ? prev : next.htmlTemplate));
      setEmailDoc(next.docJson);
    },
    [],
  );

  const updateTranslation = useCallback((langCode: string, template: string) => {
    setTranslations((prev) => {
      const next = new Map(prev);
      if (template.trim()) {
        next.set(langCode, template);
      } else {
        next.delete(langCode);
      }
      return next;
    });
  }, []);


  // ─── Derived state (memoized) ──────────────────────────────────────────────

  const hasChannels = channels.length > 0;

  const previewLanguageLabel = useMemo(
    () => languages.find(l => l.code === previewLanguage)?.nativeName || previewLanguage,
    [languages, previewLanguage],
  );

  const hasMessageErrors = !name.trim() || !messageTemplate.trim() || channels.length === 0;
  const hasMediaErrors = mediaEnabled && !mediaUrl.trim();

  const previewPayload = useMemo((): BroadcastPayload | null => {
    const resolvedMediaParseMode = mediaParseMode === 'none' ? undefined : mediaParseMode;

    const payloadMedia =
      mediaEnabled && mediaUrl.trim().length > 0
        ? { type: mediaType, url: mediaUrl.trim(), caption: mediaCaption.trim() || undefined, parseMode: resolvedMediaParseMode }
        : undefined;

    const normalizedTgButtons = buttons
      .map((b) => ({ text: b.text.trim(), url: b.url?.trim() || undefined, callbackData: b.callbackData?.trim() || undefined }))
      .filter((b) => b.text.length > 0);
    const normalizedEmailButtons = emailButtons
      .map((b) => ({ text: b.text.trim(), url: b.url?.trim() || undefined }))
      .filter((b) => b.text.length > 0);
    const normalizedAppButtons = appButtons
      .map((b) => ({ text: b.text.trim(), url: b.url?.trim() || undefined }))
      .filter((b) => b.text.length > 0);

    const trimmedEmailHtml = emailVisualEnabled ? emailHtml.trim() : '';
    const hasEmailVisual = trimmedEmailHtml.length > 0;

    const hasTg = payloadMedia || normalizedTgButtons.length > 0;
    const hasEm = normalizedEmailButtons.length > 0 || hasEmailVisual;
    const hasAp = normalizedAppButtons.length > 0;

    if (!hasTg && !hasEm && !hasAp) { return null; }

    return {
      telegram: hasTg ? { media: payloadMedia, buttons: normalizedTgButtons.length > 0 ? normalizedTgButtons : undefined } : undefined,
      email: hasEm
        ? {
            buttons: normalizedEmailButtons.length > 0 ? normalizedEmailButtons : undefined,
            html: hasEmailVisual ? trimmedEmailHtml : undefined,
            docJson: hasEmailVisual ? emailDoc : undefined,
          }
        : undefined,
      app: hasAp ? { buttons: normalizedAppButtons } : undefined,
    };
  }, [mediaEnabled, mediaUrl, mediaType, mediaCaption, mediaParseMode, buttons, emailButtons, appButtons, emailVisualEnabled, emailHtml, emailDoc]);

  // ─── Build submit payload ─────────────────────────────────────────────────

  const buildSubmitPayload = useCallback((): BroadcastCreatePayload => {
    const resolvedParseMode = parseMode === 'none' ? null : parseMode;
    const { definition: currentDefinition, selectedUserIds: currentUserIds } = recipientDataRef.current;

    const translationsArray: BroadcastTranslation[] = [...translations.entries()]
      .filter(([, template]) => template.trim().length > 0)
      .map(([langCode, template]) => ({ languageCode: langCode, messageTemplate: template.trim() }));

    // selectedUserIds из preview-таблицы передаются вместе с definition через поле includeUserIds.
    // Backend извлечёт их при resolveFiltersToWhereClause и применит OR поверх сегмента.
    const definitionWithIncludes: SegmentDefinition & { includeUserIds?: number[] } = {
      ...currentDefinition,
      ...(currentUserIds.length > 0 ? { includeUserIds: currentUserIds } : {}),
    };

    // Бэкенд при отправке в канал EMAIL рендерит messageTemplate тем же
    // Handlebars-движком (noEscape: true) и отдаёт результат как html письма
    // (notification-sender.service.ts -> transporter.sendMail({ html })).
    // payload.email.html бэкендом рассылок НЕ читается. Поэтому, чтобы
    // визуальное письмо реально уходило, его HTML должен стать
    // messageTemplate. Делаем это только когда выбран ТОЛЬКО EMAIL
    // (без TELEGRAM) — иначе HTML сломал бы рендер telegram-сообщения,
    // т.к. messageTemplate общий для каналов. payload.email.html/docJson
    // сохраняем для повторного открытия в редакторе.
    const trimmedEmailHtml = emailVisualEnabled ? emailHtml.trim() : '';
    const useVisualAsTemplate =
      trimmedEmailHtml.length > 0 &&
      channels.includes('EMAIL') &&
      !channels.includes('TELEGRAM');
    const effectiveMessageTemplate = useVisualAsTemplate
      ? trimmedEmailHtml
      : messageTemplate;

    return {
      name,
      messageTemplate: effectiveMessageTemplate,
      parseMode: resolvedParseMode,
      channels,
      payload: previewPayload ?? undefined,
      definition: definitionWithIncludes,
      scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      translations: translationsArray.length > 0 ? translationsArray : undefined,
      botId,
    };
  }, [parseMode, translations, name, messageTemplate, channels, previewPayload, scheduledAt, emailVisualEnabled, emailHtml, botId]);

  // ─── Preview ──────────────────────────────────────────────────────────────

  const handlePreview = useCallback(() => {
    if (!previewLanguage) { return; }

    const template = translations.get(previewLanguage) || messageTemplate;
    if (!template.trim()) {
      setPreviewError('Шаблон сообщения пустой');
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewResult(null);

    const { definition: previewDefinition, selectedUserIds: previewUserIds } = recipientDataRef.current;
    const previewDefinitionWithIncludes: SegmentDefinition & { includeUserIds?: number[] } = {
      ...previewDefinition,
      ...(previewUserIds.length > 0 ? { includeUserIds: previewUserIds } : {}),
    };

    fetchPreview(
      {
        url: '/admin/broadcasts/preview',
        method: 'post',
        values: {
          messageTemplate: template,
          languageCode: previewLanguage,
          definition: previewDefinitionWithIncludes,
        },
      },
      {
        onSuccess: (response) => {
          setPreviewResult(response.data.data);
          setPreviewLoading(false);
        },
        onError: (error) => {
          setPreviewError(error.message || 'Не удалось получить предпросмотр');
          setPreviewLoading(false);
        },
      },
    );
  }, [previewLanguage, translations, messageTemplate, fetchPreview]);

  const handleOpenPreview = useCallback(() => {
    setPreviewOpen(true);
    handlePreview();
  }, [handlePreview]);

  // ─── Delivery breakdown ─────────────────────────────────────────────────────
  // Считает разбивку аудитории по фактическому боту/каналу доставки для текущих
  // получателей и набора каналов. Маршрутизацию отправки не меняет — только показ.
  const handleLoadBreakdown = useCallback(() => {
    setBreakdownLoading(true);
    setBreakdownError(null);

    const { definition, selectedUserIds } = recipientDataRef.current;
    const definitionWithIncludes: SegmentDefinition & { includeUserIds?: number[] } = {
      ...definition,
      ...(selectedUserIds.length > 0 ? { includeUserIds: selectedUserIds } : {}),
    };

    fetchBreakdown(
      {
        url: '/admin/broadcasts/delivery-breakdown',
        method: 'post',
        values: {
          definition: definitionWithIncludes,
          channels,
        },
      },
      {
        onSuccess: (response) => {
          // Refine dataProvider уже разворачивает ResponseFormatter-обёртку
          // success/data → тело лежит прямо в response.data (без второго .data).
          setBreakdown(response.data);
          setBreakdownLoading(false);
        },
        onError: (error) => {
          setBreakdownError(error.message || 'Не удалось загрузить разбивку доставки');
          setBreakdownLoading(false);
        },
      },
    );
  }, [channels, fetchBreakdown]);

  // Авто-предпросмотр для постоянной панели: дебаунс при изменении
  // текста/перевода/языка. Пишет в тот же previewResult, что и диалог.
  useEffect(() => {
    if (!previewLanguage) { return; }
    const template = translations.get(previewLanguage) || messageTemplate;
    if (!template.trim()) { return; }
    const timer = setTimeout(() => { handlePreview(); }, 800);
    return () => clearTimeout(timer);
  }, [previewLanguage, messageTemplate, translations, handlePreview]);

  // ─── Return ───────────────────────────────────────────────────────────────

  return {
    // Form state + setters
    name,
    setName,
    messageTemplate,
    setMessageTemplate,
    parseMode,
    setParseMode,
    channels,
    botId,
    setBotId,
    recipientDataRef,
    recipientCount,
    scheduledAt,
    setScheduledAt,
    mediaEnabled,
    setMediaEnabled,
    mediaType,
    setMediaType,
    mediaUrl,
    setMediaUrl,
    mediaCaption,
    setMediaCaption,
    mediaParseMode,
    setMediaParseMode,
    buttons,
    emailButtons,
    appButtons,
    emailVisualEnabled,
    setEmailVisualEnabled,
    emailHtml,
    emailDoc,
    handleEmailEditorChange,
    translations,
    activeTranslationTab,
    setActiveTranslationTab,

    // Preview state
    previewOpen,
    setPreviewOpen,
    previewLanguage,
    setPreviewLanguage,
    previewResult,
    previewLoading,
    previewError,

    // Delivery breakdown state
    breakdown,
    breakdownLoading,
    breakdownError,
    handleLoadBreakdown,

    // Data
    languages,
    availableVariables,
    plans,
    courses,
    telegramChannels,

    // Callbacks
    toggleChannel,
    handleRecipientDataChange,
    addButton,
    updateButton,
    removeButton,
    addEmailButton,
    updateEmailButton,
    removeEmailButton,
    addAppButton,
    updateAppButton,
    removeAppButton,
    updateTranslation,
    handlePreview,
    handleOpenPreview,
    buildSubmitPayload,

    // Derived
    hasChannels,
    hasMessageErrors,
    hasMediaErrors,
    previewLanguageLabel,
    previewPayload,
  };
}
